import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Warehouse } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi } from 'date-fns/locale';

import { RequestMaterialItemOption, RequestMaterialLine, RequestMaterialUomOption } from '../types';

interface ExportItem {
  lineId: number;
  itemCode: string;
  itemName: string;
  requestedQty: number;
  exportQty: number;
  uomId: string;
  uomName: string;
  availableQty: number | undefined;
}

export interface WarehouseExportDraft {
  warehouseId: string;
  warehouseName: string;
  exportDate: string | null;
  items: ExportItem[];
}

interface CreateWarehouseExportModalProps {
  open: boolean;
  onClose: () => void;
  selectedLines: RequestMaterialLine[];
  itemOptions: RequestMaterialItemOption[];
  uomOptions: RequestMaterialUomOption[];
  warehouseId: string;
  warehouseName?: string;
  companyName?: string;
  defaultExportDate?: string | null;
  availableQtyByLineId?: Record<number, number>;
  onConfirm: (draft: WarehouseExportDraft) => void;
}

const formatQty = (value: number) =>
  value.toLocaleString('vi-VN', { maximumFractionDigits: 2 });

const canExport = (availableQty: number | undefined): boolean =>
  availableQty !== undefined && availableQty > 0;

export default function CreateWarehouseExportModal({
  open,
  onClose,
  selectedLines,
  itemOptions,
  uomOptions,
  warehouseId,
  warehouseName,
  companyName,
  defaultExportDate,
  availableQtyByLineId = {},
  onConfirm,
}: CreateWarehouseExportModalProps) {
  const [exportDate, setExportDate] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<ExportItem[]>([]);

  React.useEffect(() => {
    if (!open) return;

    const today = new Date().toISOString().slice(0, 10);
    setExportDate(defaultExportDate || today);

    const itemLines = selectedLines.filter((line) => line.line_type === 'item' && line.item_id);
    setItems(
      itemLines.map((line) => {
        const matchedItem = itemOptions.find((item) => item.id === line.item_id);
        const uom = uomOptions.find((u) => u.id === line.uom_id);
        const requestedQty = Number(line.requested_qty) || 0;
        const available = availableQtyByLineId[line.id];
        const exportQty = canExport(available)
          ? Math.min(requestedQty, available as number)
          : 0;
        return {
          lineId: line.id,
          itemCode: line.item_code || matchedItem?.code || '',
          itemName: line.item_name || matchedItem?.name || '',
          requestedQty,
          exportQty,
          uomId: line.uom_id || '',
          uomName: uom?.name || line.uom_name || '',
          availableQty: available,
        };
      }),
    );
  }, [open, selectedLines, itemOptions, uomOptions, defaultExportDate, availableQtyByLineId]);

  const handleQtyChange = (index: number, value: string) => {
    const raw = parseFloat(value);
    setItems((prev) => {
      const next = [...prev];
      const item = { ...next[index] };
      const available = item.availableQty as number;
      const clamped = isNaN(raw)
        ? item.exportQty
        : Math.min(Math.max(0.01, Math.round(raw * 100) / 100), available);
      item.exportQty = clamped;
      next[index] = item;
      return next;
    });
  };

  const exportableItems = items.filter((item) => canExport(item.availableQty));
  const blockedCount = items.length - exportableItems.length;

  const handleConfirm = () => {
    onConfirm({
      warehouseId,
      warehouseName: warehouseName || warehouseId,
      exportDate,
      items: exportableItems,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warehouse sx={{ color: '#059669', fontSize: '1.25rem' }} />
          <Typography variant='h6' sx={{ fontWeight: 700, color: '#111827' }}>
            Tạo phiếu xuất kho
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', mt: 0.5 }}>
          {exportableItems.length} / {items.length} vật tư đủ điều kiện xuất
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
          {/* Warning banner when some items are blocked */}
          {blockedCount > 0 && (
            <Alert severity='warning' sx={{ mb: 2.5, fontSize: '0.82rem' }}>
              <strong>{blockedCount} vật tư</strong> không đủ tồn kho (tồn khả dụng &le; 0) và sẽ không được xuất.
            </Alert>
          )}

          {/* Warehouse info + date */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, mb: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                Kho xuất
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                {warehouseName || warehouseId || '—'}
              </Typography>
              {companyName && (
                <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mt: 0.25 }}>{companyName}</Typography>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                Ngày xuất kho
              </Typography>
              <DatePicker
                value={exportDate ? new Date(exportDate) : null}
                onChange={(date) => setExportDate(date ? (date as Date).toISOString().slice(0, 10) : null)}
                format='dd/MM/yyyy'
                slotProps={{ textField: { size: 'small', placeholder: 'Chọn ngày xuất', sx: { minWidth: 180 } } }}
              />
            </Box>
          </Box>

          {/* Items table */}
          <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '40px' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '130px' }}>Mã vật tư</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Tên vật tư</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '150px' }}>
                    Số lượng xuất
                    <Typography component='span' sx={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 400, ml: 0.5 }}>
                      (YC)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '110px' }}>ĐVT</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '140px' }}>Tồn khả dụng</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const eligible = canExport(item.availableQty);
                  const available = item.availableQty;

                  return (
                    <TableRow
                      key={item.lineId}
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        backgroundColor: eligible ? 'inherit' : '#fafafa',
                        opacity: eligible ? 1 : 0.6,
                      }}
                    >
                      <TableCell sx={{ color: '#6b7280', fontSize: '0.8rem' }}>{index + 1}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: eligible ? '#374151' : '#9ca3af',
                            fontFamily: '"Roboto Mono", monospace',
                          }}
                        >
                          {item.itemCode || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.875rem', color: eligible ? '#374151' : '#9ca3af' }}>
                          {item.itemName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {eligible ? (
                          <TextField
                            size='small'
                            type='number'
                            value={item.exportQty}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                            slotProps={{
                              htmlInput: { min: 0.01, max: available, step: 0.01 },
                              formHelperText: { sx: { fontSize: '0.68rem', mx: 0, whiteSpace: 'nowrap' } },
                            }}
                            helperText={`YC: ${item.requestedQty} | Tối đa: ${formatQty(available as number)}`}
                            sx={{ width: '120px', '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.82rem', color: '#9ca3af' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.875rem', color: eligible ? '#6b7280' : '#9ca3af' }}>
                          {item.uomName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {available === undefined ? (
                          <Chip
                            label='Chưa tải tồn'
                            size='small'
                            sx={{ fontSize: '0.7rem', backgroundColor: '#f3f4f6', color: '#6b7280', height: 20 }}
                          />
                        ) : available <= 0 ? (
                          <Chip
                            label='Không đủ tồn'
                            size='small'
                            sx={{ fontSize: '0.7rem', backgroundColor: '#fee2e2', color: '#dc2626', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#059669' }}>
                            {formatQty(available)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>
                      Không có vật tư nào được chọn.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant='outlined' onClick={onClose} sx={{ textTransform: 'none' }}>
          Hủy
        </Button>
        <Button
          variant='contained'
          disabled={exportableItems.length === 0}
          onClick={handleConfirm}
          sx={{ textTransform: 'none', backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
        >
          Xác nhận xuất kho ({exportableItems.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
